import useSWR from 'swr'
import { gql, queryWithAuth } from '@crystal-eyes/utils/apis/graphqlApi'
import getAuth from '@crystal-eyes/data/getAuth'
import { PeerReviewStatus, TraitReviewName } from '@crystal-eyes/types'

interface InputIdentity {
  id: string
}

type Identity = {
  id: string
  reviewsFromPeers?: {
    average: {
      name: TraitReviewName
      score: number
    }[]
    peerReviews: {
      id: string
      status: PeerReviewStatus
      traitsReview: { name: TraitReviewName; score: number }[]
      reviewer: {
        id: string
        personalInfo: { fullName?: string; photoUrl?: string }
      }
      reviewerEmail?: string
      createdAt: string
    }[]
  }
  reviewsOfPeers?: {
    id: string
    createdAt: string
    status: PeerReviewStatus
    traitsReview: { name: TraitReviewName; score: number }[]
    identity: {
      id: string
      personalInfo: { fullName?: string; photoUrl?: string }
    }
  }[]
}

type Options = {
  identity?: InputIdentity
}

export type State = {
  data?: Identity
  loading?: boolean
  error?: any
}

export async function getPeerReviews({
  identity,
}: {
  identity: { id: string }
}): Promise<Identity | undefined> {
  const auth = await getAuth()

  if (auth.authToken) {
    return queryWithAuth(auth, PEER_REVIEWS_QUERY, {
      id: identity.id,
    }).then(resp => resp.identity)
  } else {
    return
  }
}

const PEER_REVIEWS_QUERY = gql`
  query getPeerReviews($id: Guid!) {
    identity(id: $id) {
      id
      reviewsFromPeers {
        peerReviews {
          traitsReview {
            name
            score
          }
          createdAt
          status
          id
          reviewer {
            id
            personalInfo {
              fullName
              photoUrl
            }
          }
          reviewerEmail
        }
        average {
          name
          score
        }
      }
      reviewsOfPeers {
        id
        createdAt
        status
        identity {
          id
          personalInfo {
            fullName
            photoUrl
          }
        }
        traitsReview {
          name
          score
        }
      }
    }
  }
`

export default function usePeerReview({ identity }: Options): State {
  const fetcher = ([_key, identity]: [string, { id: string }]) =>
    getPeerReviews({ identity })

  const {
    data,
    isLoading: loading,
    error,
  } = useSWR(
    identity ? ['usePeerReviews', { id: identity.id }] : null,
    fetcher,
    {
      revalidateOnFocus: false,
      keepPreviousData: true,
      fallbackData: identity,
    },
  )

  return {
    data,
    loading,
    error,
  }
}
